import { trackApparelCollectionsVideoFailedToPlay } from "@faire/web-api/events/apparelCollections/actionUnknown/videoFailedToPlay";
import { ICollectionPromo } from "@faire/web-api/indigofair/data/ICollectionPromo";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { IProductBasedCollectionPromo } from "@faire/web-api/indigofair/data/IProductBasedCollectionPromo";
import { IRetailerProductBasedCollectionResponse } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionResponse";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Grid } from "@faire/web/slate/Layout";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import React from "react";
import styled from "styled-components";

import { EfficientListLoader } from "@faire/retailer/components/EfficientListLoader/EfficientListLoader";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { ProductTileImpressionProvider } from "@faire/retailer/views/product-based-collection/__internal__/__internal__/ProductTileImpressionProvider";
import { CollectionVideoTile } from "@faire/retailer/views/product-based-collection/__internal__/CollectionVideoTile";

import { ProductBasedCollectionItem } from "../ProductBasedCollectionItem";

import { getFirstVideo } from "./utils";

interface ICollectionItemListProps {
  collection: IRetailerProductBasedCollectionResponse.IRetailerProductBasedCollection;
  productTiles: ILeanProductTile[];
  showVerticalImagery?: boolean;
  columnCount: number;
  gridGap: number;
  collectionPromo?: ICollectionPromo;
}

export const PBCEfficientBuyingItemList: React.FC<ICollectionItemListProps> = ({
  productTiles,
  collection,
  showVerticalImagery = false,
  columnCount,
  gridGap,
  collectionPromo,
}) => {
  const video = getFirstVideo(collection.videos);
  const { retailerToken } = useRetailerStore(["retailerToken"]);

  const renderProductItem = (item: ILeanProductTile) => {
    return (
      <ProductBasedCollectionItem
        key={item.product?.token}
        collection={collection!}
        productTile={item}
        showVerticalImagery={showVerticalImagery}
        collectionPromo={collectionPromo}
      />
    );
  };

  return (
    <StyledGrid
      rowGap={24}
      columnGap={gridGap}
      columns={columnCount}
      columnTemplate={(columns) => `repeat(${columns}, minmax(0, 232px));`}
    >
      <ProductTileImpressionProvider
        productBasedCollectionToken={collection.token}
        requestId={collection.request_id}
      >
        <EfficientListLoader
          data={productTiles}
          renderComponent={renderProductItem}
          batchSize={columnCount * 2}
          forceRender={isPrerender()}
          filterFakeTile
          threshold={-500}
          hashKey={collection.token}
        />
      </ProductTileImpressionProvider>
      {video &&
      collection.active_display_promo?.type !==
        IProductBasedCollectionPromo.Type.FREE_DISPLAY ? (
        <CollectionVideoTile
          columnCount={columnCount}
          tileCount={productTiles.length}
          video={video}
          onFailedToPlayVideo={() =>
            trackApparelCollectionsVideoFailedToPlay(
              retailerToken ?? "",
              collection?.token ?? "",
              video?.token ?? ""
            )
          }
        />
      ) : null}
    </StyledGrid>
  );
};

const StyledGrid = styled(Grid)`
  ${responsiveMediaQueries.tabletAndAbove} {
    grid-auto-rows: 1fr;
  }

  overflow: visible;
`;
